import React, { useMemo, useRef, useState } from "react";
import { Plus, Search, Sparkles, Maximize2, Send, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import "./askrevq.css";

// ── Mock SHAWL adjudication data (from v19 design — display only) ──
const CPT_DATA = {
  "99213": { pair: "99213 + 36415", times: "1,887×", paid: "15.2% fully paid", mod: "99213-25", modpaid: "81%", leak: "90460, 90656 partial-pays", conf: 100, recs: 52, est: "$96.40", pct: "92%", range: "$78–$112", lines: "4,210", src: "8,940",
    pdx: [["I10", 94], ["E11.9", 88], ["J45.20", 71], ["M54.5", 64]], dx2: [["Z79.4", 82], ["E78.5", 77], ["I25.10", 59]] },
  "99214": { pair: "99214 + 93000", times: "1,204×", paid: "22.6% fully paid", mod: "99214-25", modpaid: "84%", leak: "93010 bundled denials", conf: 97, recs: 71, est: "$148.10", pct: "89%", range: "$120–$168", lines: "3,180", src: "6,420",
    pdx: [["I10", 91], ["E11.9", 85], ["I25.10", 73], ["R07.9", 61]], dx2: [["E78.5", 80], ["Z79.4", 68], ["N18.3", 55]] },
  "20610": { pair: "20610 + 76942", times: "642×", paid: "31.4% fully paid", mod: "20610-RT", modpaid: "77%", leak: "J3301 units mismatch", conf: 93, recs: 38, est: "$218.00", pct: "81%", range: "$180–$248", lines: "1,140", src: "2,210",
    pdx: [["M17.11", 90], ["M25.561", 76], ["M19.011", 62]], dx2: [["M54.5", 58], ["Z79.899", 44]] },
};
const CPTS = ["99213", "99214", "20610"];
const AGES = [["0-17", 18], ["18-34", 42], ["35-49", 64], ["50-64", 88], ["65+", 54]];
export const detectCpt = (t) => (String(t || "").match(/\b(99213|99214|20610)\b/) || [])[1] || "99213";

const SEED_THREADS = [
  { id: "t1", day: "Today", title: "How can I bill 99213", msgs: [{ r: "u", t: "How can I bill 99213 to get paid?" }, { r: "a", cpt: "99213" }] },
  { id: "t2", day: "Today", title: "Best modifier for 99214 + 93000", msgs: [{ r: "u", t: "Best modifier for 99214 + 93000?" }, { r: "a", cpt: "99214" }] },
  { id: "t3", day: "Yesterday", title: "Show me all details of 20610", msgs: [{ r: "u", t: "Show me all details of 20610 joint injection" }, { r: "a", cpt: "20610" }] },
  { id: "t4", day: "Jun 12", title: "Why is Cigna denying 93000", msgs: [{ r: "u", t: "Why is Cigna denying 93000?" }, { r: "a", cpt: "99214" }] },
];

function DxRows({ title, rows }) {
  return (
    <div className="ar-pdx">
      <div className="ar-pdx-h">{title}</div>
      {rows.map(([code, pct]) => (
        <div className="ar-pdx-row" key={code}>
          <span className="ar-pdx-code">{code}</span>
          <span className="ar-pdx-bar"><i style={{ width: `${pct}%` }} /></span>
          <span className="ar-pdx-pct">{pct}%</span>
        </div>
      ))}
    </div>
  );
}

export function AssistantMsg({ cpt: initialCpt }) {
  const [cpt, setCpt] = useState(initialCpt);
  const d = CPT_DATA[cpt] || CPT_DATA["99213"];
  return (
    <div className="ar-msg-a" data-testid="revq-assistant-msg">
      <div className="ar-a-head">
        <div className="ar-a-ico"><Sparkles size={13} /></div>
        <span className="ar-a-name">SHAWL</span>
        <span className="ar-conf">{d.conf}% confidence · {d.recs} records matched</span>
      </div>
      <div className="ar-a-body">
        For <b>{cpt}</b>, historical adjudications show the strongest paid pattern below. The combination <b>{d.pair}</b> was
        billed together <b>{d.times}</b> with <b>{d.paid}</b> on first pass.
      </div>
      <div className="ar-stat">
        <span className="ar-statchip good">Best modifier: <b>{d.mod}</b> · {d.modpaid} paid</span>
        <span className="ar-statchip">Est. payment <b>{d.est}</b></span>
        <span className="ar-statchip">{d.pct} paid rate</span>
      </div>
      <div className="ar-warn">
        <AlertTriangle size={14} />
        <span>Leakage watch: {d.leak} — these partial-pay; confirm medical necessity or expect a clawback.</span>
      </div>

      <div className="ar-an">
        <div className="ar-an-top">
          <span className="ar-an-h">CPT analysis · {cpt}</span>
          <span className="ar-an-chips">
            {CPTS.map((c) => (
              <span key={c} className={`ar-cptchip ${c === cpt ? "on" : ""}`} onClick={() => setCpt(c)} data-testid={`revq-cptchip-${c}`}>{c}</span>
            ))}
          </span>
        </div>
        <div className="ar-an-grid">
          <div className="ar-an-col">
            <div>
              <div className="ar-est-l">Payment estimate</div>
              <div className="ar-est">
                <div className="ar-est-top"><span className="ar-est-amt">{d.est}</span><span className="ar-est-pct">{d.pct} paid</span></div>
                <div className="ar-est-meta">Range {d.range} · {d.lines} paid lines across {d.src} source records</div>
              </div>
            </div>
            <div>
              <div className="ar-pdx-h">Age group density</div>
              <div className="ar-age">
                {AGES.map(([lbl, h]) => (
                  <div className="ar-age-col" key={lbl}><div className="ar-age-bar" style={{ height: `${h}%` }} /><div className="ar-age-lbl">{lbl}</div></div>
                ))}
              </div>
            </div>
          </div>
          <div className="ar-an-col">
            <DxRows title="Primary Dx (PDX) · paid %" rows={d.pdx} />
            <DxRows title="Secondary Dx · paid %" rows={d.dx2} />
          </div>
        </div>
      </div>

      <div className="ar-msg-actions">
        <span className="ar-ma" onClick={() => toast.message("Re-classifying with broader cohort…")}>Re-classify</span>
        <span className="ar-ma" onClick={() => toast.message(`Verbose: showing all ${d.recs} matched records`)}>View verbose</span>
        <span className="ar-meta">1.2s · $0.004</span>
        <div className="ar-rate">
          <span onClick={() => toast.success("Thanks — feedback noted")}>Bad</span>
          <span onClick={() => toast.success("Thanks — feedback noted")}>Nice</span>
          <span onClick={() => toast.success("Marked solved ✓")}>Solved it</span>
        </div>
      </div>
    </div>
  );
}

export default function RevQ() {
  const [threads, setThreads] = useState(SEED_THREADS);
  const [activeId, setActiveId] = useState("t1");
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const [depth, setDepth] = useState("compact");
  const [full, setFull] = useState(false);
  const convRef = useRef(null);

  const active = useMemo(() => threads.find((t) => t.id === activeId) || threads[0], [threads, activeId]);

  const grouped = useMemo(() => {
    const days = {};
    threads
      .filter((t) => !query || t.title.toLowerCase().includes(query.toLowerCase()))
      .forEach((t) => { (days[t.day] = days[t.day] || []).push(t); });
    return days;
  }, [threads, query]);

  const scrollDown = () => requestAnimationFrame(() => { if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight; });

  const send = (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text) return;
    const cpt = detectCpt(text);
    setThreads((ts) => ts.map((t) => t.id === activeId
      ? { ...t, title: t.msgs.length ? t.title : text.slice(0, 42), msgs: [...t.msgs, { r: "u", t: text }, { r: "a", cpt }] }
      : t));
    setInput("");
    scrollDown();
  };

  const newChat = () => {
    const id = `t${Date.now()}`;
    setThreads((ts) => [{ id, day: "Today", title: "New chat", msgs: [] }, ...ts]);
    setActiveId(id);
  };

  const deleteThread = (id, e) => {
    e.stopPropagation();
    setThreads((ts) => {
      const next = ts.filter((t) => t.id !== id);
      if (id === activeId && next.length) setActiveId(next[0].id);
      return next.length ? next : SEED_THREADS;
    });
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="arw" data-testid="revq-page">
      <div className="ar-pgrow">
        <div className="ar-title">Ask RevQ <span className="ar-spark-badge">powered by SHAWL</span></div>
        <div className="ar-sub">Your billing oracle — ask how to bill anything and get the answer from 181k+ real adjudications.</div>
        <span className="ar-wip" data-testid="revq-wip-note">
          <AlertTriangle size={12} /> Work in progress · Dev note — the SHAWL adjudication chat is a UI preview; answers are sample data, not a live model yet.
        </span>
      </div>

      <div className={`ar-shell ${full ? "full-left" : ""}`} data-testid="revq-shell">
        {/* Left rail */}
        <div className="ar-rail">
          <div className="ar-rail-top">
            <button className="ar-newchat" onClick={newChat} data-testid="revq-new-chat"><Plus size={14} /> New chat</button>
          </div>
          <div className="ar-search">
            <Search size={13} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search past chats…" data-testid="revq-search" />
          </div>
          <div className="ar-hist" data-testid="revq-history">
            {Object.keys(grouped).length ? Object.entries(grouped).map(([day, list]) => (
              <React.Fragment key={day}>
                <div className="ar-hist-day">{day}</div>
                {list.map((t) => (
                  <div key={t.id} className={`ar-thread ${t.id === activeId ? "on" : ""}`} onClick={() => setActiveId(t.id)} data-testid={`revq-thread-${t.id}`}>
                    <span className="tt">{t.title}</span>
                    <span className="tx" onClick={(e) => deleteThread(t.id, e)}><X size={13} /></span>
                  </div>
                ))}
              </React.Fragment>
            )) : <div style={{ fontSize: 11, color: "var(--ar-gray3)", padding: "14px 8px" }}>No matching chats.</div>}
          </div>
        </div>

        {/* Center conversation */}
        <div className="ar-main">
          <div className="ar-main-top">
            <div className="ar-a-ico"><Sparkles size={13} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ar-main-t" data-testid="revq-thread-title">{active?.title || "New chat"}</div>
              <div className="ar-main-sub">SHAWL Analytics · 181,402 adjudication records embedded</div>
            </div>
            <button className="ar-iconbtn" title="Toggle full screen" onClick={() => setFull((v) => !v)} data-testid="revq-fullscreen"><Maximize2 size={15} /></button>
          </div>

          <div className="ar-conv" ref={convRef} data-testid="revq-conversation">
            {active?.msgs?.length ? active.msgs.map((m, i) => (
              m.r === "u"
                ? <div className="ar-msg-u" key={i} data-testid="revq-user-msg">{m.t}</div>
                : <AssistantMsg key={i} cpt={m.cpt} />
            )) : (
              <div className="ar-empty" data-testid="revq-empty">
                <div className="ar-empty-ic"><Sparkles size={22} /></div>
                <div className="ar-empty-t">Ask how to bill anything</div>
                <div className="ar-empty-s">Try a CPT, a combination, or a payer — SHAWL answers from 181k+ real adjudications.</div>
                <div className="ar-empty-chips">
                  {["How can I bill 99213 to get paid?", "Best modifier for 99214 + 93000?", "Show me all details of 20610"].map((q) => (
                    <button key={q} className="ar-empty-chip" onClick={() => send(q)} data-testid="revq-suggestion">{q}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="ar-composer">
            <div className="ar-cbox">
              <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
                placeholder="Ask how to bill a CPT, a combination, a payer…" data-testid="revq-input" />
              <button className="ar-send" onClick={() => send()} data-testid="revq-send"><Send size={15} /></button>
            </div>
            <div className="ar-foot">
              <span>RevQ uses SHAWL data only. Always verify with source records.</span>
              <div className="ar-depth">
                <span className={depth === "compact" ? "on" : ""} onClick={() => setDepth("compact")} data-testid="revq-depth-compact">Compact</span>
                <span className={depth === "extreme" ? "on" : ""} onClick={() => setDepth("extreme")} data-testid="revq-depth-extreme">Extreme</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
